import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, Download, Printer, Pencil, Check, HandHeart, MessageSquare, Camera, Globe2, HandCoins, ExternalLink, Link2, Eye, EyeOff, Images, Lock, Clock } from "lucide-react";
import api, { apiErr, fmtMoney, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtRange } from "../components/TripSwitcher";
import { flagEmoji } from "../lib/flags";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function TripReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [gen, setGen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [linkEdit, setLinkEdit] = useState(false);
  const [link, setLink] = useState("");
  const [curating, setCurating] = useState(false);
  const [sel, setSel] = useState([]);
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "admin" || user?.role === "team";

  useEffect(() => {
    api.get(`/trips/${id}/report`).then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e)); navigate(-1); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = async () => {
    setGen(true);
    try { const { data } = await api.post(`/trips/${id}/report/generate`); setD({ ...d, report: data }); toast.success("Fresh summary written"); }
    catch (e) { toast.error(apiErr(e)); } finally { setGen(false); }
  };
  const saveLink = async () => {
    try { const { data } = await api.put(`/trips/${id}/report`, { external_url: link }); setD({ ...d, report: data }); setLinkEdit(false); toast.success(link ? "Report link saved" : "Report link removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    try { const { data } = await api.put(`/trips/${id}/report`, { summary: draft }); setD({ ...d, report: data }); setEditing(false); toast.success("Summary saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePublish = async (val) => {
    try { const { data } = await api.put(`/trips/${id}/report`, { published: val }); setD({ ...d, report: data, published: val }); toast.success(val ? "Report published — partners can read it now" : "Report hidden — back to private draft"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveHighlights = async () => {
    try { const { data } = await api.put(`/trips/${id}/report`, { highlight_ids: sel }); setD({ ...d, report: data }); setCurating(false); toast.success("Highlights updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;

  // Partners/followers see a gentle placeholder until the team publishes the report.
  if (d.published === false && !isManager) {
    return (
      <div className="space-y-5" data-testid="trip-report-coming-soon">
        <div className="flex items-center gap-3">
          <button data-testid="tr-back" onClick={() => navigate(-1)} className="text-[var(--brand-navy)]"><ArrowLeft className="h-5 w-5" /></button>
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Trip report</div>
        </div>
        <div className="rounded-3xl p-8 text-center text-white shadow-[0_14px_40px_rgba(13,35,64,0.28)]" style={{ background: "linear-gradient(135deg,#0d2340 0%,#14365f 55%,#1f5290 100%)" }}>
          <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center mx-auto mb-4"><Clock className="h-7 w-7" /></div>
          <h1 className="font-heading font-extrabold text-2xl">The story is being written</h1>
          <p className="text-sm opacity-85 mt-2 max-w-xs mx-auto">{d.org?.org_name || "The team"} is putting together the full report of {d.trip?.trip_name}. We'll let you know the moment it's ready.</p>
        </div>
      </div>
    );
  }

  const { trip: t, stats: s, timeline, photos, report, org } = d;
  const accent = org?.accent_color || "#C9973A";
  const published = !!d.published;
  const highlights = (report.highlight_ids?.length ? report.highlight_ids : photos).slice(0, 12);
  const summary = report.summary || "";
  // Dispatch media is stored as a full "/api/files/<id>" path; don't prefix it again.
  const src = (f, w) => fileUrl(f && (f.startsWith("/api/files/") || f.startsWith("http")) ? f : `/api/files/${f}`, w);
  const openCurate = () => { setSel(report.highlight_ids?.length ? report.highlight_ids : (photos || []).slice(0, 12)); setCurating(true); };
  const toggleSel = (fid) => setSel((p) => (p.includes(fid) ? p.filter((x) => x !== fid) : (p.length >= 24 ? p : [...p, fid])));

  const downloadHtml = () => {
    const blob = new Blob([buildHtml({ t, s, timeline, photos: highlights, summary, org, accent })], { type: "text/html" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${(t.trip_name || "trip").replace(/[^\w-]+/g, "_")}_report.html`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  return (
    <div className="space-y-5 print:space-y-4" data-testid="trip-report-page">
      <div className="flex items-center gap-3 print:hidden">
        <button data-testid="tr-back" onClick={() => navigate(-1)} className="text-[var(--brand-navy)]"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1 min-w-0"><div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Trip report</div></div>
        <button data-testid="tr-download-html" onClick={downloadHtml} className="rounded-full border border-[#0d2340]/15 bg-white px-3 py-2 text-xs font-semibold text-[#0d2340] flex items-center gap-1"><Download className="h-3.5 w-3.5" /> HTML</button>
        <button data-testid="tr-download-pdf" onClick={() => window.print()} className="rounded-full accent-bg text-white px-3 py-2 text-xs font-semibold flex items-center gap-1"><Printer className="h-3.5 w-3.5" /> PDF</button>
      </div>

      {/* Draft / publish control (team only) */}
      {isManager && (
        <div data-testid="tr-publish-banner" className={`rounded-2xl p-4 flex items-center gap-3 print:hidden border ${published ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${published ? "bg-emerald-100" : "bg-amber-100"}`}>
            {published ? <Eye className="h-5 w-5 text-emerald-700" /> : <Lock className="h-5 w-5 text-amber-700" />}
          </span>
          <div className="flex-1 min-w-0">
            <div className={`font-heading font-bold ${published ? "text-emerald-900" : "text-amber-900"}`}>{published ? "Published" : "Private draft"}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{published ? "Partners can read this report now." : "Only your team can see this. Review it, then publish when you're ready."}</div>
          </div>
          {published ? (
            <button data-testid="tr-unpublish" onClick={() => togglePublish(false)} className="shrink-0 rounded-full border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 flex items-center gap-1"><EyeOff className="h-3.5 w-3.5" /> Unpublish</button>
          ) : (
            <button data-testid="tr-publish" onClick={() => togglePublish(true)} className="shrink-0 rounded-full accent-bg text-white px-4 py-2 text-xs font-semibold flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Publish</button>
          )}
        </div>
      )}

      {/* Hero */}
      <div className="rounded-3xl overflow-hidden text-white relative shadow-[0_14px_40px_rgba(13,35,64,0.28)] print:shadow-none" style={{ background: "linear-gradient(135deg,#0d2340 0%,#14365f 55%,#1f5290 100%)" }} data-testid="tr-hero">
        {highlights[0] && <img src={src(highlights[0], 1200)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />}
        <div className="relative p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">{org?.org_name}</div>
          <h1 className="font-heading font-extrabold text-3xl leading-tight mt-1">{t.trip_name}</h1>
          <div className="text-sm opacity-85 mt-1">{fmtRange(t.start_date, t.end_date)}</div>
          {s.countries?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3 text-2xl">{s.countries.map((c) => <span key={c} title={c}>{flagEmoji(c) || "📍"}</span>)}</div>}
        </div>
      </div>

      {/* Full report on the missionary's own site */}
      {(report.external_url || isAdmin) && (
        <div className="bg-white rounded-2xl p-4 shadow-[0_4px_24px_rgba(44,44,62,0.04)] print:hidden" data-testid="tr-external">
          {report.external_url && !linkEdit && (
            <a data-testid="tr-external-link" href={report.external_url} target="_blank" rel="noreferrer" className="w-full rounded-full h-12 flex items-center justify-center gap-2 font-semibold text-white text-sm" style={{ background: accent }}>
              <ExternalLink className="h-4 w-4" /> Read the full report {(() => { try { return `on ${new URL(report.external_url).hostname.replace(/^www\./, "")}`; } catch { return ""; } })()}
            </a>
          )}
          {isAdmin && (linkEdit ? (
            <div className="flex gap-2 mt-2">
              <input data-testid="tr-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://akop.live/report" className="flex-1 rounded-xl border px-3 h-10 text-sm" />
              <button data-testid="tr-link-save" onClick={saveLink} className="rounded-full accent-bg text-white px-4 text-sm font-semibold">Save</button>
              <button onClick={() => setLinkEdit(false)} className="text-sm text-muted-foreground px-2">Cancel</button>
            </div>
          ) : (
            <button data-testid="tr-link-edit" onClick={() => { setLink(report.external_url || ""); setLinkEdit(true); }} className={`text-xs font-semibold text-[#0d2340] flex items-center gap-1 ${report.external_url ? "mt-2 mx-auto" : ""}`}><Link2 className="h-3 w-3" /> {report.external_url ? "Change report link" : "Add a link to your own report (e.g. your website)"}</button>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2" data-testid="tr-stats">
        <Stat icon={MessageSquare} n={s.dispatches} l="Dispatches" />
        <Stat icon={HandHeart} n={s.prayers} l="Prayer requests" sub={s.prayers_answered ? `${s.prayers_answered} answered` : null} />
        <Stat icon={Globe2} n={s.countries?.length || 0} l="Countries" />
        <Stat icon={Camera} n={s.photos} l="Photos" />
        <Stat icon={HandHeart} n={s.prayer_moments} l="Prayer moments" sub="daily prayers prayed" />
        {s.raised_cents != null ? <Stat icon={HandCoins} n={fmtMoney(s.raised_cents, t.currency)} l="Given" sub={`${s.givers} givers`} /> : <Stat icon={HandCoins} n={s.givers ?? "—"} l="Partners" />}
      </div>

      {/* Summary */}
      <div className="bg-white rounded-3xl p-6 shadow-[0_4px_24px_rgba(44,44,62,0.04)] print:shadow-none print:border" data-testid="tr-summary">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: accent }}>The story of this journey</div>
          {isAdmin && !editing && (
            <div className="flex gap-2 print:hidden">
              <button data-testid="tr-edit" onClick={() => { setDraft(summary); setEditing(true); }} className="text-xs font-semibold text-[#0d2340] flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
              <button data-testid="tr-regenerate" disabled={gen} onClick={regenerate} className="text-xs font-semibold flex items-center gap-1" style={{ color: accent }}>{gen ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {summary ? "Rewrite with AI" : "Write with AI"}</button>
            </div>
          )}
        </div>
        {editing ? (
          <div className="mt-3 space-y-2">
            <Textarea data-testid="tr-edit-text" value={draft} onChange={(e) => setDraft(e.target.value)} className="rounded-xl min-h-[180px]" />
            <div className="flex gap-2"><button data-testid="tr-edit-save" onClick={saveEdit} className="rounded-full accent-bg text-white px-4 py-2 text-sm font-semibold flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Save</button><button onClick={() => setEditing(false)} className="text-sm text-muted-foreground px-3">Cancel</button></div>
          </div>
        ) : summary ? (
          <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-[#2C2C3E]">{summary.split(/\n+/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}</div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">{isAdmin ? "No summary yet — tap “Write with AI” or write your own." : "The team hasn't written the summary yet."}</p>
        )}
      </div>

      {/* Photo highlights */}
      {highlights.length > 0 && (
        <div data-testid="tr-photos">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Highlights</div>
            {isManager && photos.length > 0 && (
              <button data-testid="tr-curate" onClick={openCurate} className="text-xs font-semibold flex items-center gap-1 print:hidden" style={{ color: accent }}><Images className="h-3.5 w-3.5" /> Choose photos</button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {highlights.map((fid, i) => (
              <img key={fid} src={src(fid, 400)} alt="" loading="lazy" className={`w-full object-cover rounded-xl ${i % 5 === 0 ? "col-span-2 row-span-2 h-full aspect-square" : "aspect-square"}`} />
            ))}
          </div>
        </div>
      )}
      {isManager && highlights.length === 0 && photos.length > 0 && (
        <button data-testid="tr-curate-empty" onClick={openCurate} className="w-full rounded-2xl border border-dashed p-4 text-sm font-semibold flex items-center justify-center gap-2 print:hidden" style={{ color: accent }}><Images className="h-4 w-4" /> Choose highlight photos</button>
      )}

      {/* Highlight curation (team only) */}
      <Dialog open={curating} onOpenChange={setCurating}>
        <DialogContent className="rounded-3xl max-w-md p-0 overflow-hidden" data-testid="tr-curate-dialog">
          <DialogHeader className="p-5 pb-3">
            <DialogTitle className="font-heading text-[var(--brand-navy)]">Choose highlights</DialogTitle>
            <DialogDescription>Tap photos to feature them. Numbers show the order they'll appear. {sel.length}/24 selected.</DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-3 max-h-[52vh] overflow-y-auto grid grid-cols-3 gap-1.5">
            {(photos || []).map((fid) => {
              const idx = sel.indexOf(fid);
              const on = idx >= 0;
              return (
                <button key={fid} type="button" data-testid="tr-curate-photo" onClick={() => toggleSel(fid)} className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${on ? "border-[var(--tenant-accent)] ring-2 ring-[var(--tenant-accent)]/30" : "border-transparent opacity-80"}`}>
                  <img src={src(fid, 200)} alt="" loading="lazy" className="w-full h-full object-cover" />
                  {on && <span className="absolute top-1 right-1 h-5 w-5 rounded-full accent-bg text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>}
                </button>
              );
            })}
          </div>
          <div className="p-4 flex gap-2 border-t">
            <button onClick={() => setCurating(false)} className="flex-1 rounded-full border py-2.5 text-sm font-semibold text-muted-foreground">Cancel</button>
            <button data-testid="tr-curate-save" onClick={saveHighlights} className="flex-1 rounded-full accent-bg text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-1"><Check className="h-4 w-4" /> Save highlights</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] print:shadow-none print:border" data-testid="tr-timeline">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Along the way</div>
          <ol className="mt-3 space-y-2.5">
            {timeline.map((e) => (
              <li key={e.id} className="flex gap-3 text-sm">
                <span className="w-16 shrink-0 text-xs text-muted-foreground pt-0.5">{e.date ? new Date(e.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: e.is_prayer ? "#1E8A5B" : accent }} />
                <span className="text-[#2C2C3E]">{e.title}{e.country ? <span className="text-muted-foreground"> · {e.country}</span> : null}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <p className="text-center font-heading font-bold text-lg sm:text-xl text-[var(--brand-navy)] pt-2 pb-1">Thank you for walking this journey with us.</p>
      <style>{`@media print { header, nav, [data-testid="looking-back-banner"] { display: none !important; } main { padding: 0 !important; max-width: 100% !important; } }`}</style>
    </div>
  );
}

function Stat({ icon: Icon, n, l, sub }) {
  return (
    <div className="bg-white rounded-2xl p-3 shadow-[0_4px_24px_rgba(44,44,62,0.04)] print:shadow-none print:border">
      <Icon className="h-4 w-4 accent-text" />
      <div className="font-heading font-bold text-xl text-[#0d2340] mt-1.5 leading-none">{n ?? 0}</div>
      <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{l}{sub ? <span className="block opacity-80">{sub}</span> : null}</div>
    </div>
  );
}

// Self-contained HTML the partner can keep or forward.
function buildHtml({ t, s, timeline, photos, summary, org, accent }) {
  const esc = (x) => String(x ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const img = (fid, w) => fileUrl(fid && (fid.startsWith("/api/files/") || fid.startsWith("http")) ? fid : `/api/files/${fid}`, w);
  const stat = (n, l) => `<div class="s"><b>${esc(n)}</b><span>${esc(l)}</span></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(t.trip_name)} — Trip report</title>
<style>body{font-family:Georgia,serif;max-width:720px;margin:0 auto;padding:32px 20px;color:#2C2C3E;background:#FAF8F4}
h1{font-size:34px;margin:0;color:#0d2340}.sub{color:#666;margin:4px 0 20px}.hero{background:#0d2340;color:#fff;border-radius:24px;padding:28px;position:relative;overflow:hidden}
.hero img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:.35}.hero div{position:relative}.hero h1{color:#fff}.hero .sub{color:#ddd}
.stats{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:20px 0}.s{background:#fff;border-radius:16px;padding:14px}.s b{display:block;font-size:22px;color:#0d2340}.s span{font-size:12px;color:#777}
.card{background:#fff;border-radius:20px;padding:22px;margin:16px 0}.k{font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:${accent};font-weight:700}
.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}.grid img{width:100%;aspect-ratio:1;object-fit:cover;border-radius:12px}
ol{padding:0;list-style:none}li{display:flex;gap:12px;font-size:14px;margin:8px 0}li time{width:70px;color:#888;font-size:12px;flex:none}
.foot{text-align:center;color:#888;font-style:italic;margin-top:24px}</style></head><body>
<div class="hero">${photos[0] ? `<img src="${img(photos[0], 1200)}" alt="">` : ""}<div><div style="font-size:11px;letter-spacing:.2em;text-transform:uppercase;opacity:.8">${esc(org?.org_name)}</div><h1>${esc(t.trip_name)}</h1><div class="sub">${esc(fmtRange(t.start_date, t.end_date))}${s.countries?.length ? " · " + esc(s.countries.join(", ")) : ""}</div></div></div>
<div class="stats">${stat(s.dispatches, "Dispatches")}${stat(s.prayers, "Prayer requests")}${stat(s.countries?.length || 0, "Countries")}${stat(s.photos, "Photos")}${stat(s.prayer_moments, "Prayer moments")}${s.raised_cents != null ? stat(fmtMoney(s.raised_cents, t.currency), "Given") : stat(s.givers ?? "—", "Partners")}</div>
${summary ? `<div class="card"><div class="k">The story of this journey</div>${summary.split(/\n+/).filter(Boolean).map((p) => `<p>${esc(p)}</p>`).join("")}</div>` : ""}
${photos.length ? `<div class="card"><div class="k">Highlights</div><div class="grid" style="margin-top:12px">${photos.map((f) => `<img src="${img(f, 400)}" alt="">`).join("")}</div></div>` : ""}
${timeline.length ? `<div class="card"><div class="k">Along the way</div><ol>${timeline.map((e) => `<li><time>${esc(e.date)}</time><span>${esc(e.title)}${e.country ? ` · ${esc(e.country)}` : ""}</span></li>`).join("")}</ol></div>` : ""}
<p class="foot">Thank you for walking this journey with us.</p></body></html>`;
}
